/* eslint-disable lingui/no-unlocalized-strings */
import Config from "@readai/common/config";
import type { LanguageCode } from "@readai/common/types";

export const DEFAULT_LANGUAGE_CODE: LanguageCode = "en";

export const EMPTY_DELIMITER = "";

export const SPACE_DELIMITER = " ";

export const PSEUDO_LOCALE = "pseudo-LOCALE";

type SupportedLanguageCode = Exclude<
  LanguageCode,
  "yue" | typeof PSEUDO_LOCALE
>;

/**
 * This is a list of supported languages and their display names
 */
export const LOCALES: Partial<Record<LanguageCode, string>> = {
  en: "English",
  es: "Español",
  pt: "Português",
  fr: "Français",
  hi: "हिन्दी",
  it: "Italiano",
  ru: "Русский",
  de: "Deutsch",
  zh: "中文",
  ja: "日本語",
  nl: "Nederlands",
  da: "Dansk",
  fi: "Suomi",
  sv: "Svenska",
  no: "Norsk",
};

/** This object contains a key for each country code associated with their respective supported languages */
export const LOCALE_FALLBACKS: Record<string, LanguageCode> = {};

// Pseudolocalization helps us see where there are missing translations visually in the webapp
if (Config.flags.enablePseudoLocale) {
  LOCALE_FALLBACKS["zu"] = PSEUDO_LOCALE;
  LOCALE_FALLBACKS["zu-ZA"] = PSEUDO_LOCALE;
  LOCALE_FALLBACKS["pseudo-LOCALE"] = PSEUDO_LOCALE;
}

const COUNTRY_CODES_BY_LANGUAGE: Record<SupportedLanguageCode, string[]> = {
  // https://localizely.com/language-code/da/
  da: ["da-DK", "da-GL"],

  // https://localizely.com/language-code/de/
  de: ["de-AT", "de-BE", "de-CH", "de-DE", "de-IT", "de-LI", "de-LU"],

  // https://localizely.com/language-code/en/
  en: [
    "en-001",
    "en-150",
    "en-AG",
    "en-AI",
    "en-AS",
    "en-AT",
    "en-AU",
    "en-BB",
    "en-BE",
    "en-BI",
    "en-BM",
    "en-BS",
    "en-BW",
    "en-BZ",
    "en-CA",
    "en-CC",
    "en-CH",
    "en-CK",
    "en-CM",
    "en-CX",
    "en-CY",
    "en-DE",
    "en-DK",
    "en-DM",
    "en-ER",
    "en-FI",
    "en-FJ",
    "en-FK",
    "en-FM",
    "en-GB",
    "en-GD",
    "en-GG",
    "en-GH",
    "en-GI",
    "en-GM",
    "en-GU",
    "en-GY",
    "en-HK",
    "en-IE",
    "en-IL",
    "en-IM",
    "en-IN",
    "en-IO",
    "en-JE",
    "en-JM",
    "en-KE",
    "en-KI",
    "en-KN",
    "en-KY",
    "en-LC",
    "en-LR",
    "en-LS",
    "en-MG",
    "en-MH",
    "en-MO",
    "en-MP",
    "en-MS",
    "en-MT",
    "en-MU",
    "en-MW",
    "en-MY",
    "en-NA",
    "en-NF",
    "en-NG",
    "en-NL",
    "en-NR",
    "en-NU",
    "en-NZ",
    "en-PG",
    "en-PH",
    "en-PK",
    "en-PN",
    "en-PR",
    "en-PW",
    "en-RW",
    "en-SB",
    "en-SC",
    "en-SD",
    "en-SE",
    "en-SG",
    "en-SH",
    "en-SI",
    "en-SL",
    "en-SS",
    "en-SX",
    "en-SZ",
    "en-TC",
    "en-TK",
    "en-TO",
    "en-TT",
    "en-TV",
    "en-TZ",
    "en-UG",
    "en-UM",
    "en-US",
    "en-VC",
    "en-VG",
    "en-VI",
    "en-VU",
    "en-WS",
    "en-ZA",
    "en-ZM",
    "en-ZW",
  ],

  // https://localizely.com/language-code/es/
  es: [
    "es-419",
    "es-AR",
    "es-BO",
    "es-BR",
    "es-BZ",
    "es-CL",
    "es-CO",
    "es-CR",
    "es-CU",
    "es-DO",
    "es-EC",
    "es-ES",
    "es-GQ",
    "es-GT",
    "es-HN",
    "es-MX",
    "es-NI",
    "es-PA",
    "es-PE",
    "es-PH",
    "es-PR",
    "es-PY",
    "es-SV",
    "es-US",
    "es-UY",
    "es-VE",
  ],

  // https://localizely.com/language-code/fi/
  fi: ["fi-FI"],

  // https://localizely.com/language-code/fr/
  fr: [
    "fr-BE",
    "fr-BF",
    "fr-BI",
    "fr-BJ",
    "fr-BL",
    "fr-CA",
    "fr-CD",
    "fr-CF",
    "fr-CG",
    "fr-CH",
    "fr-CI",
    "fr-CM",
    "fr-DJ",
    "fr-DZ",
    "fr-FR",
    "fr-GA",
    "fr-GF",
    "fr-GN",
    "fr-GP",
    "fr-GQ",
    "fr-HT",
    "fr-KM",
    "fr-LU",
    "fr-MA",
    "fr-MC",
    "fr-MF",
    "fr-MG",
    "fr-ML",
    "fr-MQ",
    "fr-MR",
    "fr-MU",
    "fr-NC",
    "fr-NE",
    "fr-PF",
    "fr-PM",
    "fr-RE",
    "fr-RW",
    "fr-SC",
    "fr-SN",
    "fr-SY",
    "fr-TD",
    "fr-TG",
    "fr-TN",
    "fr-VU",
    "fr-WF",
    "fr-YT",
  ],

  // https://localizely.com/language-code/hi/
  hi: ["hi-IN"],

  // https://localizely.com/language-code/it/
  it: ["it-CH", "it-IT", "it-SM", "it-VA"],

  // https://localizely.com/language-code/ja/
  ja: ["ja-JP"],

  // https://localizely.com/language-code/nl/
  nl: ["nl-AW", "nl-BE", "nl-BQ", "nl-CW", "nl-NL", "nl-SR", "nl-SX"],

  // https://localizely.com/language-code/no/
  no: [],

  // https://localizely.com/language-code/pt/
  pt: [
    "pt-AO",
    "pt-BR",
    "pt-CH",
    "pt-CV",
    "pt-GQ",
    "pt-GW",
    "pt-LU",
    "pt-MO",
    "pt-MZ",
    "pt-PT",
    "pt-ST",
    "pt-TL",
  ],

  // https://localizely.com/language-code/ru/
  ru: ["ru-BY", "ru-KG", "ru-KZ", "ru-MD", "ru-RU", "ru-UA"],

  // https://localizely.com/language-code/sv/
  sv: ["sv-AX", "sv-FI", "sv-SE"],

  // https://localizely.com/language-code/zh/
  zh: [
    "zh-CN",
    "zh-TW",
    "zh-Hans",
    "zh-Hans-CN",
    "zh-Hans-HK",
    "zh-Hans-MO",
    "zh-Hans-SG",
    "zh-Hant",
    "zh-Hant-HK",
    "zh-Hant-MO",
    "zh-Hant-TW",
  ],
};

// Populate LOCALE_FALLBACKS using COUNTRY_CODES_BY_LANGUAGE
for (const [languageCode, countryCodes] of Object.entries(
  COUNTRY_CODES_BY_LANGUAGE,
)) {
  for (const countryCode of countryCodes) {
    LOCALE_FALLBACKS[countryCode] = languageCode as LanguageCode;
  }
}
